"use client";

/**
 * Ranked metric selector.
 *
 * Chrome and description placement live in IntakeOptionCard, so a metric's
 * description sits beneath its name exactly as on the problem step (ELI-1103;
 * it used to be reachable only through an info tooltip). A normalized KPI
 * calculation follows it when Argus supplies one.
 */

import { IntakeOptionCard } from "./IntakeOptionCard";

export interface MetricItem {
  name: string;
  definition?: string;
  calculation?: string;
  formula?: string[];
}

export interface IntakeMetricSelectProps {
  metrics: Array<MetricItem & { id: string }>;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  rankLabel?: string;
  /** Cap shown in the "choose up to N" hint. `null` drops the hint entirely,
   *  for surfaces where the list is a confirm-or-edit set rather than a pick-N. */
  maxSelect?: number | null;
}

export function IntakeMetricSelect({
  metrics,
  selectedIds = [],
  onToggle,
  rankLabel = "Ranked by relevance",
  maxSelect = 3,
}: IntakeMetricSelectProps) {
  return (
    <div>
      <div className="mb-[11px] text-[11px] font-semibold uppercase tracking-[.06em] text-(--fg-muted)">
        {maxSelect === null
          ? rankLabel
          : `${rankLabel} · choose up to ${maxSelect}`}
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: group has no legend and should not introduce fieldset chrome */}
      <div role="group" className="flex flex-col gap-2">
        {metrics.map((metric) => (
          <IntakeOptionCard
            key={metric.id}
            name={metric.name}
            description={metric.definition}
            footer={
              metric.calculation ? (
                <span className="mt-2 block">
                  <span className="block text-[10px] font-semibold uppercase tracking-[.06em] text-(--fg-muted)">
                    KPI calculation
                  </span>
                  <span className="mt-0.5 block text-[13px] leading-[1.5] text-(--fg-secondary)">
                    {metric.calculation}
                  </span>
                </span>
              ) : undefined
            }
            indicator="checkbox"
            selected={selectedIds.includes(metric.id)}
            onSelect={() => onToggle?.(metric.id)}
          />
        ))}
      </div>
    </div>
  );
}
